import React, { type ComponentType } from 'react';
import '@atlaskit/css-reset';
import QueryParamAdapter from '@atlassian/jira-boards-query-param-adapter/src/index.tsx';
import PageAppInitialisationAnalytics from '@atlassian/jira-common-analytics-page-app-initialisation/src/index.tsx';
import { FavoriteChangeConsumer } from '@atlassian/jira-favourite-change-provider/src/view/FavoriteChangeConsumer.tsx';
import { withSuspense } from '@atlassian/jira-platform-resources-utils/src/utils/with-suspense/index.tsx';
import { boardsDirectoryResource } from '@atlassian/jira-router-resources-directories-boards/src/index.tsx';
import SpaErrorBoundary from '@atlassian/jira-spa-apps-common/src/error-boundary/index.tsx';
import { AsyncNoSoftwareLicense } from '@atlassian/jira-spa-apps-common/src/error-screens/no-software-license/async.tsx';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';
import { LicenseCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/license/index.tsx';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { withRouter, useResource, type RouteContext } from '@atlassian/react-resource-router';
import Boards from './view/index.tsx';

// eslint-disable-next-line jira/export/restrict-types-re-export
export type Props = RouteContext;

// eslint-disable-next-line jira/react/no-usage-of-hooks-outside-components
const useBoardsDirectoryResource = withSuspense(() => useResource(boardsDirectoryResource));

export const BoardsDirectory = ({ location }: Props): React.JSX.Element => {
	const state = useBoardsDirectoryResource();
	const tenantContext = useTenantContext();

	const renderContents = () => (
		<QueryParamAdapter location={location}>
			{({ query, onQueryChanged }) => (
				<FavoriteChangeConsumer>
					{(favoriteChangeContext) => (
						<UFOSegment name="boards-directory">
							<Boards
								tenantContext={tenantContext}
								prefetchResult={{ ...state }}
								favoriteChangeContext={favoriteChangeContext}
								query={query}
								onQueryChanged={onQueryChanged}
							/>
						</UFOSegment>
					)}
				</FavoriteChangeConsumer>
			)}
		</QueryParamAdapter>
	);

	return (
		<AuthenticationCheck>
			<LicenseCheck type="software">
				{({ result }) => (result ? renderContents() : <AsyncNoSoftwareLicense />)}
			</LicenseCheck>
		</AuthenticationCheck>
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RouterWrapper: ComponentType<any> = withRouter(BoardsDirectory);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BoardsDirectoryType = (props: Record<any, any>): React.JSX.Element => (
	<>
		<MarkProductStart />
		<SpaErrorBoundary id="spa-app-bootstrap.directories.boards">
			<PageAppInitialisationAnalytics
				appNameSuffixForV2Event="directory.boards"
				appNameForV3OperationalEvent="boardsDirectory"
			/>
			<RouterWrapper {...props} />
		</SpaErrorBoundary>
	</>
);

export default BoardsDirectoryType;
